import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Users, FolderKanban, AlertCircle, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatPrice } from '@/lib/format';
import { TasksDashboardPanel } from '@/components/tasks/TasksDashboardPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useOutletContext();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/dashboard/metrics`, {
          withCredentials: true
        });
        setMetrics(response.data);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return <div className="text-stone-600">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Dashboard
        </h1>
        <p className="mt-2 text-base text-stone-600">Welcome back, {user?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border border-stone-200 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-stone-500">Total Customers</p>
              <p className="text-2xl font-semibold text-stone-900 mt-2 tabular-nums" data-testid="metric-customers">{metrics?.total_customers || 0}</p>
            </div>
            <Users className="w-10 h-10 text-amber-700" strokeWidth={1.5} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-stone-200 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-stone-500">Active Projects</p>
              <p className="text-2xl font-semibold text-stone-900 mt-2 tabular-nums" data-testid="metric-projects">{metrics?.active_projects || 0}</p>
            </div>
            <FolderKanban className="w-10 h-10 text-lime-700" strokeWidth={1.5} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-stone-200 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-stone-500">Pending Items</p>
              <p className="text-2xl font-semibold text-stone-900 mt-2 tabular-nums" data-testid="metric-pending">{metrics?.pending_items || 0}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-red-700" strokeWidth={1.5} />
          </div>
        </div>

        {user?.role === 'admin' && (
          <div className="bg-white p-6 rounded-lg border border-stone-200 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-stone-500">Total Profit</p>
                <p className="text-2xl font-semibold text-stone-900 mt-2 tabular-nums" data-testid="metric-profit">
                  ₹{metrics?.total_profit ? formatPrice(metrics.total_profit) : '0'}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-lime-700" strokeWidth={1.5} />
            </div>
          </div>
        )}
      </div>

      {['admin', 'staff'].includes(user?.role) && <TasksDashboardPanel />}

      <div className="bg-white p-6 rounded-lg border border-stone-200">
        <h2 className="text-xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Quick Stats
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-stone-500">Total Projects:</span>
            <span className="ml-2 font-medium text-stone-900 tabular-nums">{metrics?.total_projects || 0}</span>
          </div>
          {user?.role === 'admin' && (
            <div>
              <span className="text-stone-500">Total Revenue:</span>
              <span className="ml-2 font-medium text-stone-900 tabular-nums">
                ₹{metrics?.total_revenue ? formatPrice(metrics.total_revenue) : '0'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;